import { ICheckStyleProps, ICheckStyles } from '@fluentui/react/lib/Check';
import {
  IDetailsListStyleProps,
  IDetailsListStyles
} from '@fluentui/react/lib/DetailsList';
import {
  IDetailsRowStyleProps,
  IDetailsRowStyles
} from '@fluentui/react/lib/DetailsList';
import { FontWeights, IStyle, ITheme } from '@fluentui/react/lib/Styling';
import { FontSizes } from '../teams-type';
import { pxToRem } from '@fluentui/react-northstar';

export function getCheckStyles(_themeName: string, _theme: ITheme) {
  return (props: ICheckStyleProps): Partial<ICheckStyles> => {
    const { theme, checked } = props;
    const { palette } = theme;

    const styles = {
      circle: [!checked && { color: palette.neutralSecondary }],
      check: [!checked && { color: palette.neutralSecondary }]
    };

    return styles;
  };
}

export function getDetailsListStyles(themeName: string, _theme: ITheme) {
  return (props: IDetailsListStyleProps): Partial<IDetailsListStyles> => {
    const { theme } = props;
    const { palette } = theme;

    const styles = {
      root: {
        background: 'transparent',
        selectors: {
          '.ms-DetailsRow-cell': {
            // from @fluentui/react/src/themes/teams/components/Table/tableVariables.ts
            fontSize: pxToRem(13)
          },
          '.ms-GroupHeader': {
            background: palette.neutralLighterAlt,
            color: palette.black,
            borderBottom: 'none',
            selectors: {
              ':hover': {
                background: palette.neutralLighterAlt,
                color: palette.black,
                selectors: {
                  '.ms-GroupHeader-expand': {
                    color: palette.neutralDark
                  }
                }
              },
              '.ms-GroupSpacer': {
                width: '40px !important' // Needed to override inline css
              },
              '.ms-GroupHeader-check': {
                selectors: {
                  '.ms-Check': {
                    marginLeft: '13px'
                  }
                }
              },
              '.ms-GroupHeader-title': {
                fontSize: FontSizes.size14,
                fontWeight: FontWeights.bold,
                selectors: {
                  "[class^='headerCount-']": {
                    fontWeight: FontWeights.regular
                  }
                }
              },
              '.ms-GroupHeader-expand': {
                color: palette.neutralDark,
                selectors: {
                  ':hover': {
                    background: 'transparent',
                    color: palette.themePrimary,
                    selectors: {
                      i: {
                        fontWeight: FontWeights.bold
                      }
                    }
                  },
                  i: {
                    fontSize: FontSizes.size12
                  }
                }
              }
            }
          }
        }
      },
      headerWrapper: {
        selectors: {
          '.ms-DetailsHeader': {
            background: palette.neutralLighterAlt,
            borderBottomColor: palette.neutralQuaternary,
            paddingTop: '0',
            /**
             * Override min-width calculated by items-view
             */
            minWidth: '100% !important',
            selectors: {
              '.is-actionable': {
                selectors: {
                  '.ms-DetailsHeader-cellName': {
                    // from @fluentui/react/src/themes/teams/components/Table/tableVariables.ts
                    fontSize: pxToRem(12),
                    fontWeight: FontWeights.regular
                  },
                  '.ms-DetailsHeader-cellTitle': {
                    selectors: {
                      i: {
                        color: palette.black,
                        position: 'relative',
                        top: '1px'
                      } as IStyle,
                      "[data-icon-name='GroupedDescending']": {
                        paddingLeft: '6px',
                        paddingRight: '1px'
                      },
                      "[data-icon-name='page']": {
                        color: palette.neutralSecondary
                      },
                      '.ms-DetailsHeader-filterChevron': {
                        paddingTop: '1px'
                      }
                    }
                  },
                  ':hover': {
                    background: palette.neutralLighter,
                    color: palette.black,
                    selectors: {
                      '.ms-DetailsHeader-cellTitle': {
                        color: palette.black,
                        selectors: {
                          i: {
                            color: palette.black
                          }
                        }
                      }
                    }
                  }
                }
              },
              "[class*='cellIsGroupExpander-']": {
                background: 'transparent',
                color: palette.black,
                selectors: {
                  ':hover': {
                    background: palette.neutralLighter,
                    color: palette.black
                  }
                }
              },
              '.ms-DetailsHeader-cellTitle': {
                color: palette.black,
                selectors: {
                  "i[data-icon-name='ChevronDown']": {
                    color: palette.black,
                    fontSize: FontSizes.size10
                  }
                }
              },
              '.ms-DetailsHeader-filterChevron': {
                paddingTop: '2px'
              },
              ':hover .ms-DetailsHeader-cellTitle': {
                background: 'none'
              },
              '.ms-Check': {
                marginLeft: '12px'
              }
            }
          }
        }
      }
    };

    // Contrast theme overrides
    if (themeName === 'contrast') {
      styles.headerWrapper.selectors['.ms-DetailsHeader'].borderBottomColor =
        palette.neutralTertiaryAlt;

      styles.headerWrapper.selectors['.ms-DetailsHeader'].selectors[
        '.is-actionable'
      ].selectors[':hover'].background = palette.themeDark;

      styles.headerWrapper.selectors['.ms-DetailsHeader'].selectors[
        '.is-actionable'
      ].selectors[':hover'].color = palette.white;
      styles.headerWrapper.selectors['.ms-DetailsHeader'].selectors[
        '.is-actionable'
      ].selectors[':hover'].selectors['.ms-DetailsHeader-cellTitle'].selectors[
        'i'
      ].color = palette.white;
      styles.headerWrapper.selectors['.ms-DetailsHeader'].selectors[
        '.is-actionable'
      ].selectors[':hover'].selectors['.ms-DetailsHeader-cellTitle'].color =
        palette.white;
      styles.headerWrapper.selectors['.ms-DetailsHeader'].selectors[
        '.ms-DetailsHeader-cellTitle'
      ].color = palette.black;

      styles.headerWrapper.selectors['.ms-DetailsHeader'].selectors[
        "[class*='cellIsGroupExpander-']"
      ].selectors[':hover'].background = palette.themeDark;
      styles.headerWrapper.selectors['.ms-DetailsHeader'].selectors[
        "[class*='cellIsGroupExpander-']"
      ].selectors[':hover'].color = palette.white;

      // Group Header Styling
      styles.root.selectors['.ms-GroupHeader'].selectors[':hover'].color =
        theme.palette.white;
      styles.root.selectors['.ms-GroupHeader'].selectors[':hover'].background =
        palette.themeDark;
      styles.root.selectors['.ms-GroupHeader'].selectors[':hover'].selectors[
        '.ms-GroupHeader-expand'
      ].color = theme.palette.white;
      styles.root.selectors['.ms-GroupHeader'].selectors[
        '.ms-GroupHeader-expand'
      ].selectors[':hover'].color = theme.palette.white;
    }

    return styles;
  };
}

export function getDetailsRowStyles(themeName: string, _theme: ITheme) {
  return (props: IDetailsRowStyleProps): Partial<IDetailsRowStyles> => {
    const { theme, isSelected } = props;
    const { palette } = theme;

    let ROW_HOVER_BACKGROUND_COLOR = palette.neutralQuaternary;
    let SELECTED_ROW_BACKGROUND_COLOR = palette.themeLight;
    let SELECTED_HOVER_COLOR = palette.black;
    let SELECTED_COLOR = palette.black;

    const HERO_BUTTON = "[class*='heroButton']";

    // Contrast theme overrides
    if (themeName === 'contrast') {
      ROW_HOVER_BACKGROUND_COLOR = palette.themeTertiary;
      SELECTED_HOVER_COLOR = palette.white;
      SELECTED_COLOR = palette.black;
    }

    const styles = {
      root: [
        {
          background: palette.white,
          color: palette.black,
          borderBottomColor: palette.neutralLighter,
          fontSize: FontSizes.size14,
          /**
           * Override min-width calculated by items-view
           */
          minWidth: '100% !important',
          selectors: {
            '.ms-DetailsRow-cell': {
              color: palette.black,
              selectors: {
                '.ms-DetailsRow-check': {
                  height: '100%'
                },
                '.ms-Check': {
                  marginLeft: '12px'
                }
              }
            },
            '.ms-Details': {
              selectors: {
                '.ms-Button': {
                  background: 'transparent'
                }
              }
            },
            '.ms-Link': {
              // Need it to override all state (i.e. focus, hover, active) styles for the ms-link component
              color: `${palette.black} !important`
            },
            ':hover': {
              background: ROW_HOVER_BACKGROUND_COLOR,
              selectors: {
                '.ms-DetailsRow-cell': {
                  color: SELECTED_HOVER_COLOR
                },
                // Need it to override all state (i.e. focus, hover) styles for the ms-link component
                '.ms-Link': {
                  color: `${SELECTED_HOVER_COLOR} !important`
                }
              }
            },
            [HERO_BUTTON]: {
              background: 'transparent',
              selectors: {
                ':hover': {
                  background: 'transparent',
                  selectors: {
                    i: {
                      fontWeight: FontWeights.bold
                    }
                  }
                }
              }
            }
          }
        },
        isSelected && [
          {
            background: SELECTED_ROW_BACKGROUND_COLOR,
            selectors: {
              '.ms-DetailsRow-cell': {
                color: SELECTED_COLOR
              },
              // Need it to override all state (i.e. focus, hover, active) styles for the ms-link component
              '.ms-Link': {
                color: `${SELECTED_HOVER_COLOR} !important`
              },
              ':focus': {
                background: SELECTED_ROW_BACKGROUND_COLOR,
                color: SELECTED_COLOR,
                selectors: {
                  '.ms-DetailsRow-cell': {
                    color: SELECTED_COLOR
                  },
                  // Need it to override all state (i.e. focus, hover) styles for the ms-link component
                  '.ms-Link': {
                    color: `${SELECTED_HOVER_COLOR} !important`
                  },
                  ':hover': {
                    background: ROW_HOVER_BACKGROUND_COLOR
                  }
                }
              },
              ':hover': {
                background: ROW_HOVER_BACKGROUND_COLOR
              }
            }
          }
        ]
      ]
    } as Partial<IDetailsRowStyles>;

    return styles;
  };
}
